// =====================
// الاستيرادات
// =====================
import { Link } from "@remix-run/react";
import type { MetaFunction } from "@remix-run/node";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "~/shared/utils/i18n";
import { motion, AnimatePresence } from 'framer-motion';
import { getServiceKeywords, createSEODescription } from "~/shared/utils/seo-keywords";
import {
  FaSun, FaMoon, FaCalculator, FaCalendarAlt, FaFont, FaWhatsapp, FaQrcode, FaFileAlt, FaGift, FaPray, FaSquareRootAlt, FaFlag, FaHeart, FaTools, FaSearch, FaUsers, FaStar, FaRocket, FaLightbulb, FaReceipt, FaExchangeAlt, FaCog, FaUser, FaGlobe, FaEllipsisH, FaBrain, FaChartBar, FaCompressAlt, FaVideo, FaShieldAlt, FaFileContract, FaEnvelope, FaCoins, FaTicketAlt, FaDice, FaFemale, FaBaby, FaDollarSign, FaRandom, FaBookOpen, FaPlane, FaCar, FaClock
} from 'react-icons/fa';
import { AiFillHome } from 'react-icons/ai';
import "~/shared/components/dark-mode.css";
import "~/shared/components/animations.css";
import "~/shared/components/performance.css";
import "~/shared/components/mobile-performance.css";
import "~/features/services/services-page.css";

// ============================================================================
// SEO METADATA - تحسين محركات البحث
// ============================================================================

export const meta: MetaFunction = () => {
  const keywords = getServiceKeywords('services');
  const keywordsString = keywords.join(', ');
  const description = createSEODescription('services');
  
  return [
    // العنوان الرئيسي المحسن لـ SEO
    { title: "خدمات وأدوات مجانية - أسامة الزبيدي | أدوات مفيدة ومجانية | Free Tools & Services - Osama Al-Zubaidi" },
    
    // الوصف المحسن لـ SEO
    { name: "description", content: description },
    
    // الكلمات المفتاحية المحسنة
    { name: "keywords", content: keywordsString },
    
    // معلومات المؤلف
    { name: "author", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { name: "creator", content: "أسامة الزبيدي" },
    { name: "publisher", content: "أسامة الزبيدي" },
    { name: "copyright", content: "صنع بكل ❤️ في 🇸🇦" },
    
    // إعدادات الروبوتات
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    
    // معلومات اللغة والموقع
    { name: "language", content: "Arabic, English" },
    { name: "geo.region", content: "SA" },
    { name: "geo.country", content: "Saudi Arabia" },
    
    // Open Graph - محسن لـ SEO
    { property: "og:title", content: "خدمات وأدوات مجانية | أدوات مفيدة ومجانية" },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://oz.sa/services" },
    { property: "og:site_name", content: "أسامة الزبيدي - Osama Al-Zubaidi" },
    { property: "og:locale", content: "ar_AR" },
    { property: "og:locale:alternate", content: "en_US" },
    { property: "og:image", content: "https://oz.sa/logo-light.png" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "خدمات وأدوات مجانية - أسامة الزبيدي" },
    { property: "og:image:type", content: "image/png" },
    { property: "og:image:secure_url", content: "https://oz.sa/logo-light.png" },
    
    // Twitter Cards - محسن لـ SEO
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "خدمات وأدوات مجانية | أدوات مفيدة ومجانية" },
    { name: "twitter:description", content: description },
    { name: "twitter:creator", content: "@OZ7SA" },
    { name: "twitter:site", content: "@OZ7SA" },
    { name: "twitter:image", content: "https://oz.sa/logo-light.png" },
    { name: "twitter:image:alt", content: "خدمات وأدوات مجانية - أسامة الزبيدي" },
    { name: "twitter:domain", content: "oz.sa" },
    
    // معلومات إضافية لـ SEO
    { name: "canonical", content: "https://oz.sa/services" },
    { name: "alternate", content: "https://oz.sa/services", "hreflang": "ar" },
    { name: "alternate", content: "https://oz.sa/services", "hreflang": "en" },
    { name: "alternate", content: "https://oz.sa/services", "hreflang": "x-default" },
    
    // معلومات إضافية للتحسين
    { name: "rating", content: "General" },
    { name: "distribution", content: "Global" },
    { name: "coverage", content: "Worldwide" },
    { name: "target", content: "all" },
  ];
};

// =====================
// البيانات والتكوين
// =====================
// ترجمات الصفحة
const servicesTranslations = {
  ar: {
    title: "خدمات وأدوات مجانية",
    subtitle: "مجموعة من الأدوات المفيدة لمساعدتك في حياتك اليومية",
    developerMessage: "رسالة من المطور",
    developerText: "أهلاً وسهلاً بكم في عالم الأدوات المجانية! 🌟\n\nأنا أسامة الزبيدي، مطور هذه المنصة، وأسعد بتقديم مجموعة من الأدوات المفيدة التي صممتها خصيصاً لمساعدتكم في حياتكم اليومية.\n\nكل أداة هنا تم تطويرها بحب وإتقان، وأتمنى أن تجدوا فيها الفائدة المرجوة. لا تترددوا في التواصل معي لأي اقتراحات أو تحسينات! 💙",
    backToHome: "العودة للرئيسية",
    searchPlaceholder: "ابحث عن الخدمات...",
    stats: {
      totalServices: "إجمالي الخدمات",
      activeServices: "الخدمات النشطة",
      comingSoon: "قريباً",
      totalUsers: "مستخدم"
    },
    categories: {
      all: "الكل",
      calculators: "الحاسبات",
      tools: "الأدوات",
      islamic: "إسلامية",
      creative: "إبداعية"
    },
    cards: [
      {
        title: "بطاقات التهنئة",
        description: "إنشاء بطاقات تهنئة بأسهل طريقة",
        icon: "gift",
        route: "/greeting-cards",
        category: "creative",
        featured: true
      },
      {
        title: "السيرة الذاتية الذكية",
        description: "إنشاء السيرة الذاتية الذكية بأسهل طريقة",
        icon: "resume",
        route: "/resume",
        category: "creative",
        featured: true
      },
      {
        title: "أذكاري",
        description: "أذكار الصباح والمساء وأذكار أخرى",
        icon: "prayer",
        route: "/adhkar",
        category: "islamic",
        featured: true
      },
      {
        title: "مولد الفواتير",
        description: "إنشاء فواتير احترافية بسهولة",
        icon: "invoice",
        route: "/invoice",
        category: "tools",
        featured: true
      },
      {
        title: "كوبونات وأكواد خصم",
        description: "وفّر أموالك مع أكثر من 500 كود خصم من أشهر المتاجر",
        icon: "coupons",
        route: "/coupons",
        category: "tools",
        featured: true,
        disabled: true
      },
      {
        title: "تاريخ اليوم",
        description: "التاريخ الهجري والميلادي مع الوقت الحالي",
        icon: "calendar",
        route: "/today-date",
        category: "tools",
        featured: true
      },
      {
        title: "حاسبة العمر",
        description: "احسب عمرك بالتقويم الميلادي والهجري مع معلومات إضافية",
        icon: "calculator",
        route: "/age-calculator",
        category: "calculators",
        featured: true
      },
      {
        title: "حاسبة الفرق بين التواريخ",
        description: "احسب الفرق بين أي تاريخين بالتقويم الميلادي والهجري",
        icon: "date-diff",
        route: "/date-difference",
        category: "calculators",
        featured: true
      },
      {
        title: "مواعيد السعودية",
        description: "الوقت المتبقي للتواريخ المهمة في المملكة",
        icon: "saudi",
        route: "/saudi-dates",
        category: "islamic"
      },
      {
        title: "تفقيط الأعداد",
        description: "تحويل الأعداد إلى نصوص مكتوبة.",
        icon: "font",
        route: "/tafqit",
        category: "tools"
      },
      {
        title: "واتس مباشر",
        description: "إرسال رسائل سريعة إلى أي جهاز يعمل بنظام التشغيل Android.",
        icon: "whatsapp",
        route: "/quick-whats",
        category: "tools"
      },
      {
        title: "مولد رمز الاستجابة السريعة",
        description: "إنشاء رمز الاستجابة السريعة بأسهل طريقة.",
        icon: "qr",
        route: "/qr-generator",
        category: "tools"
      },
      {
        title: "محول التاريخ",
        description: "تحويل التواريخ بين التقويم الميلادي والهجري",
        icon: "exchange",
        route: "/date-converter",
        category: "tools"
      },
      {
        title: "اختصار رابط",
        description: "تقصير الروابط الطويلة بسهولة",
        icon: "link",
        route: "/link-shortener",
        category: "tools",
        disabled: true
      },
      {
        title: "تنزيل وسائط",
        description: "تحميل الفيديوهات والصور من مواقع التواصل",
        icon: "download",
        route: "/media-downloader",
        category: "tools",
        disabled: true
      },
      {
        title: "مولد كلمة مرور",
        description: "إنشاء كلمات مرور قوية وآمنة",
        icon: "password",
        route: "/password-generator",
        category: "tools",
        disabled: true
      },
      {
        title: "أدوات PDF",
        description: "تحرير وتحويل ملفات PDF",
        icon: "pdf",
        route: "/pdf-tools",
        category: "tools",
        disabled: true
      },
      {
        title: "بريد مؤقت",
        description: "إنشاء بريد إلكتروني مؤقت للاستخدام المؤقت",
        icon: "temp-email",
        route: "/temp-mail",
        category: "tools",
        featured: true
      },
      {
        title: "حاسبة الزكاة",
        description: "حساب الزكاة الواجبة على الأموال والأصول المختلفة",
        icon: "zakat",
        route: "/zakat-calculator",
        category: "islamic"
      },
      {
        title: "كاشف نصوص الذكاء الاصطناعي",
        description: "كشف النصوص المكتوبة بواسطة الذكاء الاصطناعي",
        icon: "ai",
        route: "/ai-text-detector",
        category: "tools",
        disabled: true
      },
      {
        title: "محلل البيانات",
        description: "تحليل البيانات وإنشاء الرسوم البيانية والتقارير",
        icon: "analytics",
        route: "/data-analytics",
        category: "tools"
      },
      {
        title: "القرآن الكريم",
        description: "تلاوة واستعراض سور القرآن الكريم",
        icon: "quran",
        route: "/quran",
        category: "islamic",
        disabled: true
      },
      {
        title: "مواقيت الصلاة",
        description: "اعرف مواقيت الصلاة حسب موقعك",
        icon: "prayer-times",
        route: "/prayer-times",
        category: "islamic",
        disabled: true
      },
      {
        title: "دليل رحلاتك",
        description: "أدوات وخدمات تساعدك في التخطيط للسفر",
        icon: "travel",
        route: "/travel-guide",
        category: "tools",
        disabled: true
      },
      {
        title: "اعرف عطل سيارتك",
        description: "مساعد تشخيص أعطال السيارة بشكل مبسط",
        icon: "car-diagnosis",
        route: "/car-diagnosis",
        category: "tools",
        disabled: true
      },
      {
        title: "حاسبة مكافأة نهاية الخدمة",
        description: "حساب مكافأة نهاية الخدمة وفق الأنظمة المحلية",
        icon: "eos-calculator",
        route: "/eos-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "القرعة العشوائية",
        description: "مولد القرعة العشوائية لاختيار الفائزين أو اتخاذ القرارات",
        icon: "random",
        route: "/random-picker",
        category: "tools",
        disabled: true
      },
      {
        title: "حاسبة الدورة الشهرية",
        description: "حاسبة موعد الدورة الشهرية وأيام الخصوبة",
        icon: "cycle",
        route: "/cycle-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "حاسبة موعد الولادة",
        description: "حاسبة موعد الولادة المتوقع ومتابعة الحمل",
        icon: "pregnancy",
        route: "/pregnancy-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "محول العملات",
        description: "تحويل العملات بأسعار الصرف الحالية",
        icon: "currency",
        route: "/currency-converter",
        category: "tools",
        disabled: true
      }
    ]
  },
  en: {
    title: "Free Services & Tools",
    subtitle: "A collection of useful tools to help you in your daily life",
    developerMessage: "Message from Developer",
    developerText: "Welcome to the world of free tools! 🌟\n\nI'm Osama Al-Zubaidi, the developer of this platform, and I'm happy to present a collection of useful tools that I designed specifically to help you in your daily life.\n\nEvery tool here was developed with love and precision, and I hope you find the intended benefit in them. Don't hesitate to contact me for any suggestions or improvements! 💙",
    backToHome: "Back to Home",
    searchPlaceholder: "Search services...",
    stats: {
      totalServices: "Total Services",
      activeServices: "Active Services",
      comingSoon: "Coming Soon",
      totalUsers: "User"
    },
    categories: {
      all: "All",
      calculators: "Calculators",
      tools: "Tools",
      islamic: "Islamic",
      creative: "Creative"
    },
    cards: [
      {
        title: "Greeting Cards",
        description: "Create greeting cards the easiest way",
        icon: "gift",
        route: "/greeting-cards",
        category: "creative",
        featured: true
      },
      {
        title: "Smart Resume",
        description: "Create a smart resume the easiest way",
        icon: "resume",
        route: "/resume",
        category: "creative",
        featured: true
      },
      {
        title: "Adhkar",
        description: "Morning and evening adhkar and other remembrances",
        icon: "prayer",
        route: "/adhkar",
        category: "islamic",
        featured: true
      },
      {
        title: "Invoice Generator",
        description: "Create professional invoices easily",
        icon: "invoice",
        route: "/invoice",
        category: "tools",
        featured: true
      },
      {
        title: "Coupons & Discount Codes",
        description: "Save money with over 500 discount codes from top stores",
        icon: "coupons",
        route: "/coupons",
        category: "tools",
        featured: true,
        disabled: true
      },
      {
        title: "Today's Date",
        description: "Hijri and Gregorian dates with current time",
        icon: "calendar",
        route: "/today-date",
        category: "tools",
        featured: true
      },
      {
        title: "Age Calculator",
        description: "Calculate your age in Gregorian and Hijri calendars with extra info",
        icon: "calculator",
        route: "/age-calculator",
        category: "calculators",
        featured: true
      },
      {
        title: "Date Difference Calculator",
        description: "Calculate the difference between any two dates in Gregorian and Hijri",
        icon: "date-diff",
        route: "/date-difference",
        category: "calculators",
        featured: true
      },
      {
        title: "Saudi Dates",
        description: "Time remaining for important dates in the Kingdom",
        icon: "saudi",
        route: "/saudi-dates",
        category: "islamic"
      },
      {
        title: "Tafqit (Numbers to Text)",
        description: "Convert numbers to written text.",
        icon: "font",
        route: "/tafqit",
        category: "tools"
      },
      {
        title: "Quick WhatsApp",
        description: "Send quick messages to any Android device.",
        icon: "whatsapp",
        route: "/quick-whats",
        category: "tools"
      },
      {
        title: "QR Code Generator",
        description: "Create QR codes the easiest way.",
        icon: "qr",
        route: "/qr-generator",
        category: "tools"
      },
      {
        title: "Date Converter",
        description: "Convert dates between Gregorian and Hijri calendars",
        icon: "exchange",
        route: "/date-converter",
        category: "tools"
      },
      {
        title: "Link Shortener",
        description: "Shorten long links easily",
        icon: "link",
        route: "/link-shortener",
        category: "tools",
        disabled: true
      },
      {
        title: "Media Downloader",
        description: "Download videos and images from social media",
        icon: "download",
        route: "/media-downloader",
        category: "tools",
        disabled: true
      },
      {
        title: "Password Generator",
        description: "Create strong and secure passwords",
        icon: "password",
        route: "/password-generator",
        category: "tools",
        disabled: true
      },
      {
        title: "PDF Tools",
        description: "Edit and convert PDF files",
        icon: "pdf",
        route: "/pdf-tools",
        category: "tools",
        disabled: true
      },
      {
        title: "Temporary Email",
        description: "Create temporary email for temporary use",
        icon: "temp-email",
        route: "/temp-mail",
        category: "tools",
        featured: true
      },
      {
        title: "Zakat Calculator",
        description: "Calculate obligatory zakat on different assets and wealth",
        icon: "zakat",
        route: "/zakat-calculator",
        category: "islamic"
      },
      {
        title: "AI Text Detector",
        description: "Detect written text by artificial intelligence",
        icon: "ai",
        route: "/ai-text-detector",
        category: "tools",
        disabled: true
      },
      {
        title: "Data Analytics",
        description: "Analyze data and create charts and reports",
        icon: "analytics",
        route: "/data-analytics",
        category: "tools"
      },
      {
        title: "Quran",
        description: "Recitation and browsing Quran surahs",
        icon: "quran",
        route: "/quran",
        category: "islamic",
        disabled: true
      },
      {
        title: "Prayer Times",
        description: "Get prayer times based on your location",
        icon: "prayer-times",
        route: "/prayer-times",
        category: "islamic",
        disabled: true
      },
      {
        title: "Travel Guide",
        description: "Tools and services to help you plan trips",
        icon: "travel",
        route: "/travel-guide",
        category: "tools",
        disabled: true
      },
      {
        title: "Car Diagnosis",
        description: "Simple assistant to diagnose car issues",
        icon: "car-diagnosis",
        route: "/car-diagnosis",
        category: "tools",
        disabled: true
      },
      {
        title: "End of Service Calculator",
        description: "Calculate end-of-service benefits per local rules",
        icon: "eos-calculator",
        route: "/eos-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "Random Picker",
        description: "Random picker generator for selecting winners or making decisions",
        icon: "random",
        route: "/random-picker",
        category: "tools",
        disabled: true
      },
      {
        title: "Menstrual Cycle Calculator",
        description: "Calculate menstrual cycle dates and fertility periods",
        icon: "cycle",
        route: "/cycle-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "Pregnancy Calculator",
        description: "Calculate expected delivery date and pregnancy tracking",
        icon: "pregnancy",
        route: "/pregnancy-calculator",
        category: "calculators",
        disabled: true
      },
      {
        title: "Currency Converter",
        description: "Convert currencies with current exchange rates",
        icon: "currency",
        route: "/currency-converter",
        category: "tools",
        disabled: true
      },
      {
        title: "حاسبة الزكاة",
        description: "حساب الزكاة الواجبة على الأموال والأصول المختلفة",
        icon: "zakat",
        route: "/zakat-calculator",
        category: "islamic"
      }
    ]
  },

  
};

// ألوان الودجت
const widgetColors = {
  primary: {
    light: 'bg-gradient-to-br from-purple-500 to-purple-600',
    dark: 'dark:from-purple-600 dark:to-purple-700',
    text: 'text-white',
    hover: 'hover:from-purple-600 hover:to-purple-700',
    shadow: 'shadow-purple-500/20'
  },
  secondary: {
    light: 'bg-gradient-to-br from-pink-500 to-pink-600',
    dark: 'dark:from-pink-600 dark:to-pink-700',
    text: 'text-white',
    hover: 'hover:from-pink-600 hover:to-pink-700',
    shadow: 'shadow-pink-500/20'
  },
  success: {
    light: 'bg-gradient-to-br from-green-500 to-green-600',
    dark: 'dark:from-green-600 dark:to-green-700',
    text: 'text-white',
    hover: 'hover:from-green-600 hover:to-green-700',
    shadow: 'shadow-green-500/20'
  },
  warning: {
    light: 'bg-gradient-to-br from-orange-500 to-orange-600',
    dark: 'dark:from-orange-600 dark:to-orange-700',
    text: 'text-white',
    hover: 'hover:from-orange-600 hover:to-orange-700',
    shadow: 'shadow-orange-500/20'
  },
  danger: {
    light: 'bg-gradient-to-br from-red-500 to-red-600',
    dark: 'dark:from-red-600 dark:to-red-700',
    text: 'text-white',
    hover: 'hover:from-red-600 hover:to-red-700',
    shadow: 'shadow-red-500/20'
  },
  info: {
    light: 'bg-gradient-to-br from-blue-500 to-blue-600',
    dark: 'dark:from-blue-600 dark:to-blue-700',
    text: 'text-white',
    hover: 'hover:from-blue-600 hover:to-blue-700',
    shadow: 'shadow-blue-500/20'
  },
  dark: { // Added dark mode for temp-email
    light: 'bg-gradient-to-br from-gray-700 to-gray-800',
    dark: 'dark:from-gray-800 dark:to-gray-900',
    text: 'text-white',
    hover: 'hover:from-gray-800 hover:to-gray-900',
    shadow: 'shadow-gray-500/20'
  }
};

// تكوين الخدمات - مطابق تماماً للصفحة الرئيسية
const serviceConfigs = [
  {
    id: 'greeting-cards',
    colorScheme: 'danger' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'resume',
    colorScheme: 'primary' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'saudi-dates',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'age-calculator',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'date-difference',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'tafqit',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'whatsapp',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'qr-generator',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  {
    id: 'adhkar',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'date-converter',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const
  },
  {
    id: 'today-date',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'large' as const
  },
  {
    id: 'data-analytics',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'large' as const // بطاقة كبيرة للخدمات المميزة
  },
  {
    id: 'invoice',
    colorScheme: 'primary' as keyof typeof widgetColors,
    size: 'small' as const // بطاقة صغيرة للخدمات العادية
  },
  // New disabled services
  {
    id: 'quran',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'prayer-times',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'travel-guide',
    colorScheme: 'secondary' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'car-diagnosis',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'eos-calculator',
    colorScheme: 'primary' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },

  {
    id: 'coupons',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'large' as const, // بطاقة كبيرة للخدمات المميزة
    disabled: true
  },
  {
    id: 'link-shortener',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'media-downloader',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'password-generator',
    colorScheme: 'danger' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'pdf-tools',
    colorScheme: 'secondary' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'temp-mail',
    colorScheme: 'dark' as keyof typeof widgetColors,
    size: 'large' as const
  },
  {
    id: 'zakat-calculator',
    colorScheme: 'success' as keyof typeof widgetColors,
    size: 'small' as const
  },
  {
    id: 'ai-text-detector',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  // duplicate removed
  {
    id: 'random-picker',
    colorScheme: 'secondary' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'cycle-calculator',
    colorScheme: 'danger' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'pregnancy-calculator',
    colorScheme: 'info' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  },
  {
    id: 'currency-converter',
    colorScheme: 'warning' as keyof typeof widgetColors,
    size: 'small' as const,
    disabled: true
  }
];

// =====================
// الأدوات والمساعدات
// =====================
// إدارة الوضع الليلي
function useDarkMode() {
  const [isDark, setIsDark] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // قراءة الحالة المحفوظة أو استخدام تفضيل النظام
    const savedTheme = localStorage.getItem('theme');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && systemPrefersDark);
    setIsDark(shouldBeDark);
    
    // تطبيق الحالة على DOM
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    setIsLoaded(true);
  }, []);

  const toggleDarkMode = useCallback(() => {
    const newDarkMode = !isDark;
    setIsDark(newDarkMode);
    
    // حفظ الحالة
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
    
    // تطبيق الحالة على DOM
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  return { isDark, isLoaded, toggleDarkMode };
}

// =====================
// مكونات الأيقونات - محسنة للهوية البصرية
// =====================
function ServiceIcon({ icon, className = '' }: { icon: string; className?: string }) {
  const iconClasses = `w-full h-full flex items-center justify-center ${className}`;
  
  switch (icon) {
    case 'quran':
      return <div className={iconClasses}><FaBookOpen className="w-4/5 h-4/5" /></div>;
    case 'prayer-times':
      return <div className={iconClasses}><FaClock className="w-4/5 h-4/5" /></div>;
    case 'travel':
      return <div className={iconClasses}><FaPlane className="w-4/5 h-4/5" /></div>;
    case 'car-diagnosis':
      return <div className={iconClasses}><FaCar className="w-4/5 h-4/5" /></div>;
    case 'eos-calculator':
      return <div className={iconClasses}><FaCoins className="w-4/5 h-4/5" /></div>;
    case 'exchange':
      return <div className={iconClasses}><FaExchangeAlt className="w-4/5 h-4/5" /></div>;
    case 'calculator':
      return <div className={iconClasses}><FaCalculator className="w-4/5 h-4/5" /></div>;
    case 'calendar':
      return <div className={iconClasses}><FaCalendarAlt className="w-4/5 h-4/5" /></div>;
    case 'font':
      return <div className={iconClasses}><FaFont className="w-4/5 h-4/5" /></div>;
    case 'whatsapp':
      return <div className={iconClasses}><FaWhatsapp className="w-4/5 h-4/5" /></div>;
    case 'qr':
      return <div className={iconClasses}><FaQrcode className="w-4/5 h-4/5" /></div>;
    case 'resume':
      return <div className={iconClasses}><FaFileAlt className="w-4/5 h-4/5" /></div>;
    case 'gift':
      return <div className={iconClasses}><FaGift className="w-4/5 h-4/5" /></div>;
    case 'prayer':
      return <div className={iconClasses}><FaPray className="w-4/5 h-4/5" /></div>;
    case 'math':
      return <div className={iconClasses}><FaSquareRootAlt className="w-4/5 h-4/5" /></div>;
    case 'saudi':
      return <div className={iconClasses}><FaFlag className="w-4/5 h-4/5" /></div>;
    case 'invoice':
      return <div className={iconClasses}><FaReceipt className="w-4/5 h-4/5" /></div>;
    case 'more':
      return <div className={iconClasses}><FaEllipsisH className="w-4/5 h-4/5" /></div>;
    case 'ai':
      return <div className={iconClasses}><FaBrain className="w-4/5 h-4/5" /></div>;
    case 'analytics':
      return <div className={iconClasses}><FaChartBar className="w-4/5 h-4/5" /></div>;
    case 'link':
      return <div className={iconClasses}><FaCompressAlt className="w-4/5 h-4/5" /></div>; // أيقونة ضغط/تقصير للروابط
    case 'download':
      return <div className={iconClasses}><FaVideo className="w-4/5 h-4/5" /></div>; // أيقونة فيديو للوسائط
    case 'password':
      return <div className={iconClasses}><FaShieldAlt className="w-4/5 h-4/5" /></div>; // أيقونة درع للأمان
    case 'pdf':
      return <div className={iconClasses}><FaFileContract className="w-4/5 h-4/5" /></div>; // أيقونة ملف عقد للـ PDF
    case 'temp-email':
      return <div className={iconClasses}><FaEnvelope className="w-4/5 h-4/5" /></div>; // أيقونة مغلف للبريد
    case 'zakat':
      return <div className={iconClasses}><FaCoins className="w-4/5 h-4/5" /></div>; // أيقونة زكاة لحاسبة الزكاة
    case 'coupons':
      return <div className={iconClasses}><FaTicketAlt className="w-4/5 h-4/5" /></div>; // أيقونة كوبون للكوبونات
    case 'random':
      return <div className={iconClasses}><FaDice className="w-4/5 h-4/5" /></div>; // أيقونة نرد للقرعة العشوائية
    case 'cycle':
      return <div className={iconClasses}><FaFemale className="w-4/5 h-4/5" /></div>; // أيقونة امرأة للدورة الشهرية
    case 'pregnancy':
      return <div className={iconClasses}><FaBaby className="w-4/5 h-4/5" /></div>; // أيقونة طفل للحمل
    case 'currency':
      return <div className={iconClasses}><FaDollarSign className="w-4/5 h-4/5" /></div>; // أيقونة دولار للعملات
    case 'date-diff':
      return <div className={iconClasses}><FaCalendarAlt className="w-4/5 h-4/5" /></div>;
    default:
      return <div className={iconClasses}><FaTools className="w-4/5 h-4/5" /></div>;
  }
}

// =====================
// مكونات الصفحة
// =====================
// بطاقة الخدمة
export interface ServiceCardProps {
  card: {
    title: string;
    description: string;
    icon: string;
    route: string;
    category?: string;
    featured?: boolean;
    disabled?: boolean;
  };
  config: {
    colorScheme: keyof typeof widgetColors;
    size: 'small' | 'large';
    disabled?: boolean;
  };
  lang: 'ar' | 'en';
  index: number;
}

function ServiceCard({ card, config, lang, index }: ServiceCardProps) {
  const colors = widgetColors[config.colorScheme];
  
  // تحديد حجم البطاقة - نوعين فقط (كبير وصغير) للتوافق مع الهوية البصرية
  const getWidgetSize = () => {
    switch (config.size) {
      case 'large':
        return {
          iconSize: 'w-12 h-12 sm:w-16 sm:h-16 md:w-18 md:h-18 lg:w-20 lg:h-20 xl:w-22 xl:h-22',
          titleSize: 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl',
          descriptionSize: 'text-sm sm:text-base md:text-lg lg:text-xl',
          padding: 'p-6 sm:p-8 md:p-10 lg:p-12 xl:p-14',
          iconMargin: 'mb-4 sm:mb-5 md:mb-6 lg:mb-8'
        };
      default: // small
        return {
          iconSize: 'w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 xl:w-16 xl:h-16',
          titleSize: 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl',
          descriptionSize: 'text-xs sm:text-sm md:text-base lg:text-lg',
          padding: 'p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10',
          iconMargin: 'mb-3 sm:mb-4 md:mb-5 lg:mb-6'
        };
    }
  };

  const sizes = getWidgetSize();
  const isDisabled = config.disabled || card.disabled;

  if (isDisabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.5, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`
          relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600
          bg-gray-100 dark:bg-gray-800 cursor-not-allowed
          ${config.size === 'large' ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
          ${sizes.padding}
        `}
      >
        {/* Featured Badge for Disabled Cards */}
        {card.featured && (
          <div className="absolute top-2 right-2 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 px-2 py-1 rounded-full text-xs font-bold z-10 opacity-50">
            ✨ {lang === 'ar' ? 'مميز' : 'Featured'}
          </div>
        )}
        
        <div className="flex flex-col items-center justify-center h-full text-center">
          {/* أيقونة الخدمة في منتصف البطاقة */}
          <div className="flex-grow flex items-center justify-center w-full">
            <div className={`${sizes.iconSize} flex items-center justify-center text-gray-400 dark:text-gray-500`}>
              <ServiceIcon icon={card.icon} className="flex-shrink-0" />
            </div>
          </div>
          
          {/* النص في أسفل البطاقة */}
          <div className="flex-shrink-0 space-y-2 w-full">
            <h3 className={`${sizes.titleSize} font-bold text-gray-400 dark:text-gray-500`}>
              {card.title}
            </h3>
            
            <p className={`${sizes.descriptionSize} text-gray-400 dark:text-gray-500`}>
              {card.description}
            </p>
            
            <div className="mt-4 px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-full">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {lang === 'ar' ? 'قريباً' : 'Coming Soon'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <Link 
      to={card.route} 
      prefetch="intent"
      className={`
        block relative overflow-hidden rounded-2xl cursor-pointer group card-mobile-optimized touch-optimized
        ${colors.light} ${colors.dark} ${colors.text}
        ${config.size === 'large' ? 'col-span-2 row-span-2' : 'col-span-1 row-span-1'}
        ${sizes.padding}
        transition-all duration-300 ease-out
        hover:shadow-2xl ${colors.shadow}
        ${colors.hover}
        transform hover:scale-[1.02] hover:-translate-y-1
        active:scale-[0.98] active:translate-y-0
        focus:outline-none focus:ring-4 focus:ring-white/20
        touch-manipulation
        ${card.featured ? 'ring-2 ring-yellow-400/30 ring-offset-2 ring-offset-transparent' : ''}
      `}
      style={{ 
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className={`flex flex-col items-center justify-center h-full text-center min-h-full relative z-10 p-2 ${card.featured ? 'featured-card' : ''}`}
      >
        {/* Featured Badge - Enhanced */}
        {card.featured && (
          <motion.div
            initial={{ opacity: 0, scale: 0, rotate: -12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20, 
              delay: index * 0.1 + 0.3 
            }}
            className="absolute -top-1 -right-1 z-20 group/badge"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400 to-orange-400 blur-sm opacity-75 group-hover/badge:opacity-100 transition-opacity duration-300" />
            
            {/* Main Badge */}
            <div className="relative flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-400 text-yellow-900 rounded-lg shadow-lg transform group-hover/badge:scale-105 transition-all duration-300">
              {/* Sparkle Icon */}
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="text-xs"
              >
                ✨
              </motion.div>
              
              {/* Text */}
              <span className="text-xs font-bold tracking-wide">
                {lang === 'ar' ? 'مميز' : 'HOT'}
              </span>
              
              {/* Pulse Dot */}
              <motion.div
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-yellow-900 rounded-full"
              />
            </div>
            
            {/* Corner Ribbon Effect */}
            <div className="absolute -top-1 -right-1 w-3 h-3">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-500 rounded-full transform rotate-45" />
            </div>
          </motion.div>
        )}
        
        {/* أيقونة الخدمة في منتصف البطاقة دائماً */}
        <div className="flex-grow flex items-center justify-center w-full">
          <div className={`${sizes.iconSize} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
            <ServiceIcon icon={card.icon} className="flex-shrink-0" />
          </div>
        </div>
        
        {/* النص في أسفل البطاقة */}
        <div className="flex-shrink-0 space-y-2 w-full">
          <h3 className={`${sizes.titleSize} font-bold transition-transform duration-300 group-hover:scale-105`}>
            {card.title}
          </h3>
          
          <p className={`${sizes.descriptionSize} opacity-90`}>
            {card.description}
          </p>
        </div>
      </motion.div>
      
      {/* تأثيرات بصرية إضافية */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10 group-hover:scale-150 transition-transform duration-500" />
      
      {/* Featured Card Special Effects */}
      {card.featured && (
        <>
          {/* Subtle glow for featured cards */}
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-orange-400/5 rounded-2xl" />
          
          {/* Animated border sparkles */}
          <motion.div
            animate={{ 
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              repeatType: 'reverse' 
            }}
            className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent bg-[length:200%_200%] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          />
        </>
      )}
    </Link>
  );
}

// إحصائيات الخدمات - محسن للأداء
function StatsSection({ data, lang }: { data: { cards: ServiceCardProps['card'][]; stats: { totalServices: string; activeServices: string; totalUsers: string; }; }; lang: 'ar' | 'en' }) {
  // حساب الإحصائيات مرة واحدة فقط - محسن للأداء
  const stats = useMemo(() => [
    {
      icon: FaTools,
      value: data.cards.length,
      label: data.stats.totalServices,
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: FaRocket,
      value: data.cards.filter((card) => !card.disabled).length,
      label: data.stats.activeServices,
      color: 'text-green-600 dark:text-green-400'
    },
    {
      icon: FaStar,
      value: data.cards.filter((card) => card.featured).length,
      label: lang === 'ar' ? 'خدمات مميزة' : 'Featured Services',
      color: 'text-yellow-600 dark:text-yellow-400'
    },
    {
      icon: FaUsers,
      value: '1000+',
      label: data.stats.totalUsers,
      color: 'text-purple-600 dark:text-purple-400'
    }
  ], [data.cards, data.stats, lang]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 text-center shadow-lg"
        >
          <stat.icon className={`w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 ${stat.color}`} />
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
        </motion.div>
      ))}
    </div>
  );
}

// البحث والتصفية - محسن للأداء
function SearchAndFilter({ searchTerm, setSearchTerm, selectedCategory, setSelectedCategory, categories, lang }: { searchTerm: string; setSearchTerm: (term: string) => void; selectedCategory: string; setSelectedCategory: (category: string) => void; categories: Record<string, string>; lang: 'ar' | 'en'; }) {
  // تحسين الأداء - استخدام useCallback للدوال
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, [setSearchTerm]);

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category);
  }, [setSelectedCategory]);

  return (
    <div className="mb-6 sm:mb-8 space-y-3 sm:space-y-4">
      {/* Search Bar */}
      <div className="relative max-w-sm sm:max-w-md mx-auto">
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
        <input
          type="text"
          placeholder={lang === 'ar' ? 'ابحث عن الخدمات...' : 'Search services...'}
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
        />
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
        {Object.entries(categories).map(([key, value]) => (
          <button
            key={key}
            onClick={() => handleCategoryChange(key)}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
              selectedCategory === key
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {value as string}
          </button>
        ))}
      </div>
    </div>
  );
}

// مكون العناصر العائمة المحسن للأداء
function FloatingElements() {
  const elements = useMemo(() => 
    Array.from({ length: 4 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: `${i * 0.8}s`,
      duration: `${3 + i * 0.5}s`
    })), []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {elements.map(({ id, left, top, delay, duration }) => (
        <div
          key={id}
          className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-10 animate-pulse"
          style={{
            left,
            top,
            animationDelay: delay,
            animationDuration: duration
          }}
        />
      ))}
    </div>
  );
}

// =====================
// الصفحة الرئيسية
// =====================
export default function ServicesPage() {
  const { currentLanguage, toggleLanguage } = useTranslation();
  const { isDark, isLoaded, toggleDarkMode } = useDarkMode();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNav, setShowNav] = useState(true);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // تحسين الأداء - استخدام useCallback للدوال
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    
    // إظهار/إخفاء القائمة الرئيسية حسب التمرير
    if (scrollY > 100) {
      setShowNav(false); // نزول للأسفل
    } else {
      setShowNav(true); // صعود للأعلى
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const servicesData = servicesTranslations[currentLanguage as keyof typeof servicesTranslations];
  
  // تصفية الخدمات حسب البحث والفئة - محسن للأداء
  const filteredServices = useMemo(() => {
    return servicesData.cards.filter((card: ServiceCardProps['card']) => {
      const matchesSearch = card.title.toLowerCase().includes(searchTerm.toLowerCase()) || card.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || card.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [servicesData.cards, searchTerm, selectedCategory]);

  // عدم عرض المحتوى حتى يتم تحميل الوضع الليلي
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300 bg-white dark:bg-gray-900">
      {/* Desktop Navigation Bar */}
      <div className="hidden md:block fixed top-3 left-0 right-0 z-50 px-4 sm:px-6 md:px-8">
        <motion.nav
          initial={{ y: 0, opacity: 1 }}
          animate={showNav ? { y: 0, opacity: 1 } : { y: -80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-4xl mx-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex gap-2 sm:gap-4 md:gap-6 px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full bg-gradient-to-r from-indigo-900/80 via-blue-800/70 to-indigo-600/70 backdrop-blur-lg shadow-2xl border border-white/10
            text-base sm:text-lg md:text-xl xl:text-2xl
            overflow-x-auto whitespace-nowrap justify-center items-center relative">
            
            {/* Main Navigation Items - Centered */}
            <motion.div 
              className="flex gap-2 sm:gap-4 md:gap-6 items-center justify-center"
              animate={showSettingsMenu ? { opacity: 0, scale: 0.8, x: -20 } : { opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Link
                to="/"
                className="flex items-center gap-1 sm:gap-2 text-white/80 hover:text-white transition flex-shrink-0 px-3 sm:px-6 py-1.5 sm:py-2 rounded-full
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
                style={{ boxShadow: undefined }}
              >
                <AiFillHome className="w-5 h-5 sm:w-6 sm:h-6" /> {currentLanguage === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <button
                className={`flex items-center gap-1 sm:gap-2 text-white font-bold px-3 sm:px-6 py-1.5 sm:py-2 rounded-full bg-indigo-700/80 transition text-base sm:text-lg md:text-xl flex-shrink-0
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200`}
                style={{ boxShadow: undefined }}
              >
                <FaTools className="w-4 h-4 sm:w-5 sm:h-5" /> {currentLanguage === 'ar' ? 'الخدمات' : 'Services'}
              </button>
              <button
                className="flex items-center gap-1 sm:gap-2 text-white/80 hover:text-white transition flex-shrink-0 px-3 sm:px-6 py-1.5 sm:py-2 rounded-full
                  hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
                style={{ boxShadow: undefined }}
              >
                <FaUser className="w-4 h-4 sm:w-5 sm:h-5" /> {currentLanguage === 'ar' ? 'عنّي' : 'About'}
              </button>
            </motion.div>
            
            {/* Settings Menu - Horizontal */}
            <motion.div 
              className="absolute right-4 flex gap-2 sm:gap-4 items-center"
              initial={{ opacity: 0, scale: 0.8, x: 20 }}
              animate={showSettingsMenu ? { opacity: 1, scale: 1, x: 0 } : { opacity: 0, scale: 0.8, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Language Toggle */}
              <button 
                onClick={() => {
                  toggleLanguage();
                  setShowSettingsMenu(false);
                }}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title={currentLanguage === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
              >
                <FaGlobe className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              {/* Dark Mode Toggle */}
              <button 
                onClick={() => {
                  toggleDarkMode();
                  setShowSettingsMenu(false);
                }}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title={isDark ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع المظلم'}
              >
                {isDark ? (
                  <FaSun className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <FaMoon className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
              
              {/* Admin Dashboard Link */}
              <Link
                to="/admin"
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title={currentLanguage === 'ar' ? 'لوحة تحكم المطور' : 'Developer Dashboard'}
              >
                <FaCog className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
              
              {/* Close Settings Button */}
              <button 
                onClick={() => setShowSettingsMenu(false)}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10 flex-shrink-0"
                title="إغلاق الإعدادات"
              >
                <span className="text-lg font-bold">×</span>
              </button>
            </motion.div>
            
            {/* Settings Toggle Button - Absolute */}
            <motion.div 
              className="absolute right-4 settings-menu"
              animate={showSettingsMenu ? { opacity: 0, scale: 0.8 } : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <button 
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-white/80 hover:text-white transition-all duration-300 p-1 rounded-full hover:bg-white/10"
                title="الإعدادات"
              >
                <FaCog className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </motion.div>
          </div>
        </motion.nav>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-2 pb-2">
        <motion.nav
          initial={{ y: 0, opacity: 1 }}
          animate={showNav ? { y: 0, opacity: 1 } : { y: 80, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-md mx-auto"
        >
          <div className="flex gap-2 justify-center items-center px-4 py-3 rounded-2xl bg-gradient-to-r from-indigo-900/90 via-blue-800/80 to-indigo-600/80 backdrop-blur-xl shadow-2xl border border-white/20 relative overflow-x-auto">
            {/* Main Navigation Items - Centered (مطابقة للديسكتوب) */}
            <Link
              to="/"
              className="flex items-center gap-1 text-white/80 hover:text-white transition flex-shrink-0 px-3 py-2 rounded-full hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
              style={{ boxShadow: undefined }}
            >
              <AiFillHome className="w-5 h-5" /> {currentLanguage === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <button
              className={`flex items-center gap-1 text-white font-bold px-3 py-2 rounded-full bg-indigo-700/80 transition text-base flex-shrink-0 hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200`}
              style={{ boxShadow: undefined }}
            >
              <FaTools className="w-5 h-5" /> {currentLanguage === 'ar' ? 'الخدمات' : 'Services'}
            </button>
            <button
              className="flex items-center gap-1 text-white/80 hover:text-white transition flex-shrink-0 px-3 py-2 rounded-full hover:shadow-2xl hover:bg-indigo-600 focus:shadow-2xl focus:bg-indigo-600 duration-200"
              style={{ boxShadow: undefined }}
            >
              <FaUser className="w-5 h-5" /> {currentLanguage === 'ar' ? 'عنّي' : 'About'}
            </button>
          </div>
        </motion.nav>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-900" />
        
        {/* Floating Elements */}
        <FloatingElements />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
              {servicesData.title}
            </h1>
            <p className="text-base sm:text-xl md:text-2xl lg:text-3xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto px-4">
              {servicesData.subtitle}
            </p>
          </motion.div>

          {/* Stats Section */}
          <StatsSection data={servicesData} lang={currentLanguage as 'ar' | 'en'} />

          {/* Search and Filter */}
          <SearchAndFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={servicesData.categories}
            lang={currentLanguage as 'ar' | 'en'}
          />

          {/* Services Grid */}
          <AnimatePresence mode="wait">
            {filteredServices.length > 0 ? (
              <motion.div
                key={`${searchTerm}-${selectedCategory}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-7xl mx-auto"
              >
                {/* ترتيب يدوي حسب التخطيط المطلوب - البطاقات المفعلة في الأعلى */}
                {/* الصف الأول - الخدمات المميزة */}
                {(() => { const card = filteredServices.find(c => c.icon === 'gift'); const config = serviceConfigs.find(c => c.id === 'greeting-cards'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={0} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'resume'); const config = serviceConfigs.find(c => c.id === 'resume'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={1} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'temp-email'); const config = serviceConfigs.find(c => c.id === 'temp-mail'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={2} /> : null; })()}
                {/* الصف الثاني - الخدمات النشطة */}
                {(() => { const card = filteredServices.find(c => c.icon === 'saudi'); const config = serviceConfigs.find(c => c.id === 'saudi-dates'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={3} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'calculator'); const config = serviceConfigs.find(c => c.id === 'age-calculator'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={4} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'date-diff' || (c.icon === 'calendar' && c.route === '/date-difference')); const config = serviceConfigs.find(c => c.id === 'date-difference'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={5} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'font'); const config = serviceConfigs.find(c => c.id === 'tafqit'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={6} /> : null; })()}
                {/* الصف الثالث - الخدمات النشطة */}
                {(() => { const card = filteredServices.find(c => c.icon === 'whatsapp'); const config = serviceConfigs.find(c => c.id === 'whatsapp'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={7} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'qr'); const config = serviceConfigs.find(c => c.id === 'qr-generator'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={8} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'prayer'); const config = serviceConfigs.find(c => c.id === 'adhkar'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={9} /> : null; })()}
                {/* الصف الرابع - الخدمات النشطة */}
                {(() => { const card = filteredServices.find(c => c.icon === 'invoice'); const config = serviceConfigs.find(c => c.id === 'invoice'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={10} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'exchange'); const config = serviceConfigs.find(c => c.id === 'date-converter'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={11} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'calendar' && c.route === '/today-date'); const config = serviceConfigs.find(c => c.id === 'today-date'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={12} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'analytics'); const config = serviceConfigs.find(c => c.id === 'data-analytics'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={13} /> : null; })()}
                {(() => { const card = filteredServices.find(c => c.icon === 'zakat'); const config = serviceConfigs.find(c => c.id === 'zakat-calculator'); return card && config ? <ServiceCard card={card} config={config} lang={currentLanguage as 'ar' | 'en'} index={14} /> : null; })()}
                
                {/* باقي الخدمات الأخرى */}
                {filteredServices
                  .filter(card => ![
                    'gift', 'resume', 'temp-email', 'saudi', 'calculator', 'font', 'whatsapp', 'qr', 'prayer', 'invoice', 'exchange', 'analytics', 'zakat'
                  ].includes(card.icon) && !(card.icon === 'calendar' && (card.route === '/today-date' || card.route === '/date-difference')) && card.icon !== 'date-diff')
                  .map((card, index) => {
                    const routeId = card.route.replace('/', '').replace('-', '');
                    let config = serviceConfigs.find(c => c.id === routeId);
                    if (!config) {
                      config = serviceConfigs.find(c => card.route.includes(c.id));
                    }
                    if (!config) {
                      // تكوين افتراضي للخدمات الجديدة
                      config = { colorScheme: 'info' as keyof typeof widgetColors, size: 'small' as const };
                    }
                    return (
                      <ServiceCard 
                        key={card.route} 
                        card={card} 
                        config={config} 
                        lang={currentLanguage as 'ar' | 'en'} 
                        index={15 + index} 
                      />
                    );
                  })}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <FaLightbulb className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                  {currentLanguage === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                </h3>
                <p className="text-gray-500 dark:text-gray-500">
                  {currentLanguage === 'ar' 
                    ? 'جرب البحث بكلمات مختلفة أو اختر فئة أخرى' 
                    : 'Try searching with different words or select another category'
                  }
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Developer Message Section */}
      <section className="py-8 sm:py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="developer-message rounded-2xl sm:rounded-3xl p-4 sm:p-8 md:p-12 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-purple-900"
          >
            <div className="flex items-center mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3 sm:mr-4">
                <FaHeart className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                {servicesData.developerMessage}
              </h2>
            </div>
            <div className="text-gray-700 dark:text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-line">
              {servicesData.developerText}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

